import type { Rewrite } from "next/dist/lib/load-custom-routes";

import {
  DEV_PHOENIX_API_PROXY_PATH,
  DEV_PHOENIX_WS_PROXY_URL_PATH,
  deriveWsUrlFromApi,
  normalizeWsUrl,
} from "./devProxy";

export const NEXT_PUBLIC_ENABLE_DEV_PROXY = "NEXT_PUBLIC_ENABLE_DEV_PROXY";

const DEV_PROXY_ENABLED = "true";

type PhoenixInfraEnv = {
  NEXT_PUBLIC_API_URL?: string;
  NEXT_PUBLIC_WS_URL?: string;
  NEXT_PUBLIC_ENABLE_DEV_PROXY?: "true" | "false";
} & Record<string, string | undefined>;

const trimEnvValue = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === "" ? null : trimmed;
};

const stripTrailingSlashes = (value: string): string =>
  value.replace(/\/+$/, "");

export const getPhoenixUpstreamApiUrl = (
  env: PhoenixInfraEnv
): string | null => {
  const apiUrl = trimEnvValue(env.NEXT_PUBLIC_API_URL);
  return apiUrl === null ? null : stripTrailingSlashes(apiUrl);
};

export const getPhoenixUpstreamWsUrl = (
  env: PhoenixInfraEnv
): string | null => {
  const explicitWsUrl = trimEnvValue(env.NEXT_PUBLIC_WS_URL);
  if (explicitWsUrl !== null) {
    return normalizeWsUrl(explicitWsUrl);
  }

  const apiUrl = env.NEXT_PUBLIC_API_URL ?? "";
  const derivedWsUrl = normalizeWsUrl(deriveWsUrlFromApi(apiUrl));
  return derivedWsUrl || null;
};

export const getPhoenixUpstreamWsRewriteDestination = (
  env: PhoenixInfraEnv
): string | null => {
  const wsUrl = getPhoenixUpstreamWsUrl(env);
  if (wsUrl === null) return null;

  const url = new URL(wsUrl);
  if (url.protocol === "ws:") {
    url.protocol = "http:";
  } else if (url.protocol === "wss:") {
    url.protocol = "https:";
  }

  return url.toString();
};

export const shouldUseClientDevProxy = (): boolean =>
  process.env.NEXT_PUBLIC_ENABLE_DEV_PROXY === DEV_PROXY_ENABLED;

export const buildPhoenixDevProxyRewrites = (
  env: PhoenixInfraEnv,
  isEnabled: boolean
): Rewrite[] => {
  if (!isEnabled) return [];

  const apiUrl = getPhoenixUpstreamApiUrl(env);
  const wsRewriteDestination = getPhoenixUpstreamWsRewriteDestination(env);

  return [
    ...(wsRewriteDestination === null
      ? []
      : [
          {
            source: DEV_PHOENIX_WS_PROXY_URL_PATH,
            destination: wsRewriteDestination,
          },
        ]),
    ...(apiUrl === null
      ? []
      : [
          {
            source: `${DEV_PHOENIX_API_PROXY_PATH}/:path*`,
            destination: `${apiUrl}${DEV_PHOENIX_API_PROXY_PATH}/:path*`,
          },
          {
            source: "/exchange/:path*",
            destination: `${apiUrl}/exchange/:path*`,
          },
          {
            source: "/trader/:path*",
            destination: `${apiUrl}/trader/:path*`,
          },
          {
            source: "/market/:path*",
            destination: `${apiUrl}/market/:path*`,
          },
        ]),
  ];
};
